import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';

import { Text, Avatar, Indicator, Checkbox, Radio } from 'modules/common';

import * as c from './constants';

function MenuItem({
  label,
  indicatorType,
  leadingType,
  leadingItem,
  avatarSrc,
  withLeading,
  trailingItem,
  withTrailing,
  disabled,
  value,
  selectType,
  onClick,
  className,
  readOnly,
  textProps,
}) {
  const renderTrailingItem = () => {
    const props = {
      textColor: 'text-placeholder',
    };

    if (typeof trailingItem === 'function') {
      return trailingItem?.(props);
    }

    return trailingItem;
  };

  const renderLeading = {
    avatar: (
      <Avatar
        src={avatarSrc}
        size="16px"
        className="object-cover"
        radius="rounded"
      />
    ),
    indicator: <Indicator size="md" type={indicatorType} withBorderWhite />,
    select:
      selectType === 'radio' ? (
        <Radio value={value} type="secondary" disabled={disabled} />
      ) : (
        <Checkbox value={value} type="secondary" disabled={disabled} />
      ),
  };

  return (
    <button
      className={cn(
        'p-ds-12 bg-primitive-white w-full text-left duration-100 ease-out-in transition-all',
        {
          'cursor-not-allowed !bg-primitive-white': disabled,
          'cursor-default': readOnly,
          'focus:bg-primitive-white focus:ring-2 focus:ring-default ring-inset hover:bg-brand-secondary-muted':
            !readOnly,
        },
        className
      )}
      type="button"
      onClick={readOnly || disabled ? () => {} : onClick}
    >
      <div
        className={cn(
          'flex items-center gap-ds-8',
          disabled ? 'text-disabled' : 'text-muted'
        )}
      >
        {withLeading && <>{leadingItem || renderLeading?.[leadingType]}</>}

        <Text {...textProps} className={cn('flex-1', textProps?.className)}>
          {label}
        </Text>

        {withTrailing && renderTrailingItem()}
      </div>
    </button>
  );
}

MenuItem.defaultProps = {
  withLeading: true,
  withTrailing: true,
  label: '',
  indicatorType: 'default',
  leadingType: '',
  leadingItem: false,
  avatarSrc: '',
  trailingItem: false,
  disabled: false,
  className: '',
  selectType: 'radio',
  onClick: false,
  value: false,
  readOnly: false,
  textProps: {},
};

MenuItem.propTypes = {
  withLeading: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  value: PropTypes.bool,
  className: PropTypes.string,
  textProps: PropTypes.instanceOf(Object),
  withTrailing: PropTypes.bool,
  selectType: PropTypes.oneOf(c.SELECT_TYPE_OPTS),
  onClick: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  leadingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  trailingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
  ]),
  avatarSrc: PropTypes.string,
  indicatorType: PropTypes.oneOf(Object.keys(c.INDICATOR_TYPE_OPTS)),
  leadingType: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.oneOf(c.LEADING_TYPE),
  ]),
};

export default MenuItem;
